import { createContext, useContext, useState, useEffect } from 'react';
import { API_BASE as API } from '../config';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [screenAccess, setScreenAccess] = useState([]);
  const [dashboards, setDashboards] = useState([]); // dashboards visible to the current user
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  const loadDashboards = (tok) => {
    fetch(`${API}/dashboards`, { headers: { Authorization: `Bearer ${tok}` } })
      .then(r => r.ok ? r.json() : [])
      .then(setDashboards)
      .catch(() => setDashboards([]));
  };

  useEffect(() => {
    if (token) {
      fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : Promise.reject())
        .then(data => {
          setUser(data);
          setScreenAccess(data.screenAccess || []);
          loadDashboards(token);
          setLoading(false);
        })
        .catch(() => { logout(); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, [token]);

  const login = async (username, password) => {
    const res = await fetch(`${API}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username, password })
    });
    if (!res.ok) throw new Error('Invalid credentials');
    const data = await res.json();
    localStorage.setItem('token', data.token);
    setToken(data.token);
    setUser(data.user);
    setScreenAccess(data.screenAccess || []);
    loadDashboards(data.token);
  };

  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    setScreenAccess([]);
    setDashboards([]);
  };

  const isHrManager = user?.role === 'HR Manager';

  const canView = (screen) => {
    if (user?.role === 'Admin') return true;
    if (isHrManager && (screen === 'employees' || screen === 'dashboard')) return true;
    return screenAccess.some(s => s.screen === screen && s.can_view);
  };

  const canCreate = (screen) => {
    if (user?.role === 'Admin') return true;
    if (isHrManager && screen === 'employees') return true;
    return screenAccess.some(s => s.screen === screen && s.can_create);
  };

  const canEdit = (screen) => {
    if (user?.role === 'Admin') return true;
    if (isHrManager && screen === 'employees') return true;
    return screenAccess.some(s => s.screen === screen && s.can_edit);
  };

  const canDelete = (screen) => {
    if (user?.role === 'Admin') return true;
    if (isHrManager && screen === 'employees') return true;
    return screenAccess.some(s => s.screen === screen && s.can_delete);
  };

  return (
    <AuthContext.Provider value={{ user, token, login, logout, loading, screenAccess, dashboards, canView, canCreate, canEdit, canDelete }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
export { API };
